import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, LabelList, ReferenceLine, ReferenceArea, Cell } from "recharts";
import { Scale, TrendingDown, TrendingUp, Activity, Pencil, Camera, Images, X, Save, Loader2, ChevronLeft, ChevronRight, ChevronDown, Plus, ImagePlus, Trash2, Target, Share2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import { fmtInt, todayISO } from "@/lib/utils";
import { toast } from "sonner";
import ShareProgressModal from "@/components/ShareProgressModal";
import TraineeProfileCard from "@/components/TraineeProfileCard";
import SharedCalendarCard from "@/components/SharedCalendarCard";
import SharedMealsCard from "@/components/SharedMealsCard";
import {
  VIEW_MODES,
  AR_MONTHS_FULL,
  AR_DAYS,
  EN_DAYS,
  AR_MONTHS_TIP,
  EN_MONTHS_FULL,
  fmtIsoAr,
  chartTooltipTitle,
  bmiCategory,
  mondayOf,
  toISO,
  fmtDayShort,
  KG_TO_LB,
  kgToDisplay,
  displayToKg,
  unitLabel,
  unitLabelEn,
} from "@/lib/progressUtils";
import { makeTooltip, makeBalanceTooltip, makeMacroTooltip } from "@/components/progress/tooltips";
import WeightLogModal from "@/components/progress/WeightLogModal";
import GoalWeightModal from "@/components/progress/GoalWeightModal";
import { ChartCard, AxisHint, Legend } from "@/components/progress/ChartCard";
import { AvgRow, BarValueRow } from "@/components/progress/rows";
import BmiScale from "@/components/progress/BmiScale";
import GoalTimeline from "@/components/progress/GoalTimeline";

export default function Progress({ isSharedMode = false, shareCfg = null } = {}) {
  const { t, lang } = useI18n();
  const { user, refresh } = useAuth();
  const [viewMode, setViewMode] = useState("week");
  // Balance card starts in simple mode — one clear insight for beginners.
  // User can tap "المزيد" to reveal TDEE, day-counts, cumulative, tips.
  const [balanceDetailsOpen, setBalanceDetailsOpen] = useState(false);
  const [weightDetailsOpen, setWeightDetailsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Independent cursors per view
  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [monthCursor, setMonthCursor] = useState(() => ({ year: today.getFullYear(), month: today.getMonth() + 1 }));
  const [yearCursor, setYearCursor] = useState(() => today.getFullYear());

  const [data, setData] = useState(null);
  const [trendAll, setTrendAll] = useState(null);   // {week, month, year} aggregates for Reality-Check compare-table
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [weightHistory, setWeightHistory] = useState([]);
  const [goalHistory, setGoalHistory] = useState([]);

  const reloadSummary = () => {
    const q = new URLSearchParams();
    if (viewMode === "week") {
      q.set("range", "week");
      q.set("week_start", toISO(weekStart));
    } else if (viewMode === "month") {
      q.set("range", "month");
      q.set("year", monthCursor.year);
      q.set("month", monthCursor.month);
    } else {
      q.set("range", "year");
      q.set("year", yearCursor);
    }
    api.get(`/progress/summary?${q.toString()}`).then(({ data }) => setData(data)).catch(() => setData(null));
  };

  // Fetch week + month + year aggregates in parallel — used by the Reality-Check
  // compare-table so the user sees all three horizons at a glance regardless of
  // which chart period they've selected. Refetched whenever the local weight
  // history changes (i.e. after logging or editing a weight).
  const reloadTrendCompare = () => {
    Promise.all([
      api.get("/progress/summary?range=week"),
      api.get("/progress/summary?range=month"),
      api.get("/progress/summary?range=year"),
    ]).then(([w, m, y]) => {
      const pick = (d) => ({
        active_days: Number(d?.active_days_excl_today ?? d?.active_days ?? 0),
        est_kg:      Number(d?.est_weight_change_kg_excl_today ?? 0),
        avg_balance: Number(d?.avg_balance_excl_today ?? 0),
      });
      setTrendAll({ week: pick(w.data), month: pick(m.data), year: pick(y.data) });
    }).catch(() => setTrendAll(null));
  };

  useEffect(() => {
    reloadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, weekStart, monthCursor, yearCursor,
      // Refetch summary whenever the user's TDEE / target-calories / goal / include-exercise flag changes.
      // Ensures the Reality-Check + Calorie-Balance chart always use the LATEST profile-derived numbers,
      // even if the user updates goals on another page and returns to Progress.
      user?.tdee, user?.daily_calories, user?.goal, user?.include_exercise_calories,
      user?.current_weight_kg, user?.target_weight_kg]);

  // Safety net: refresh Progress data when the user returns to the tab OR when the
  // page becomes visible again (e.g. after logging an exercise on Dashboard then
  // switching back). This prevents "nothing changed" surprises where a route stays
  // mounted and the Chart/Reality-Check show stale numbers.
  useEffect(() => {
    const onFocus = () => reloadSummary();
    const onVis = () => { if (document.visibilityState === "visible") reloadSummary(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, weekStart, monthCursor, yearCursor]);

  // Load recent progress photos (all angles, latest first)
  const loadRecentPhotos = () => {
    api.get("/progress-photos").then(({ data }) => {
      setRecentPhotos(Array.isArray(data) ? data.slice(0, 4) : []);
    }).catch(() => setRecentPhotos([]));
  };
  useEffect(() => { loadRecentPhotos(); }, []);

  // Load weight history — scope to current view (week=90d, month=180d, year=730d).
  // Extracted as a named function so weight save + unit toggle can trigger a live reload.
  const loadWeightHistory = () => {
    const days = viewMode === "week" ? 90 : (viewMode === "month" ? 180 : 730);
    return api.get(`/weight?days=${days}`).then(({ data }) => {
      setWeightHistory(Array.isArray(data?.entries) ? data.entries : []);
    }).catch(() => setWeightHistory([]));
  };
  useEffect(() => { loadWeightHistory(); }, [viewMode]);

  // Load goal-weight history (all changes over time) — powers the stepped goal line
  const loadGoalHistory = () => {
    api.get("/user/goal-history").then(({ data }) => {
      setGoalHistory(Array.isArray(data?.entries) ? data.entries : []);
    }).catch(() => setGoalHistory([]));
  };
  useEffect(() => { loadGoalHistory(); }, [user?.target_weight_kg]);

  // Fetch week/month/year trend aggregates once on mount + whenever key inputs
  // change. These power the "compare all three horizons" table inside
  // Reality-Check, so the user sees week+month+year progress in one view.
  useEffect(() => {
    reloadTrendCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tdee, user?.daily_calories, user?.goal, user?.include_exercise_calories,
      user?.current_weight_kg, user?.target_weight_kg, weightHistory.length]);

  // Return the goal weight that was active on `iso` (YYYY-MM-DD)
  const goalAt = useMemo(() => {
    const sorted = [...goalHistory].sort((a, b) => (a.effective_from || "").localeCompare(b.effective_from || ""));
    return (iso) => {
      if (!iso || sorted.length === 0) return user?.target_weight_kg || null;
      let active = null;
      for (const g of sorted) {
        if ((g.effective_from || "") <= iso) active = g.target_weight_kg;
        else break;
      }
      // Before the first history entry, fall back to the earliest known previous_target_kg
      // so the line accurately reflects the goal at that point in time (not the latest goal).
      if (active == null) {
        return sorted[0].previous_target_kg ?? sorted[0].target_weight_kg;
      }
      return active;
    };
  }, [goalHistory, user?.target_weight_kg]);

  // Aggregate weight entries into the same bucketing as the other charts:
  // - Week view: 7 daily buckets labeled with day names (اثنين..الأحد)
  // - Month view: 5 weekly buckets labeled "أسبوع 1..5"
  // - Year view: 12 monthly buckets labeled "1..12"
  // Aggregation rule per bucket: LOWEST for lose goal, HIGHEST for gain goal, LATEST for maintain
  const weightSeries = useMemo(() => {
    if (!weightHistory.length) return [];

    // First, dedupe: for each date, keep the reading with the latest created_at
    const perDay = new Map();
    for (const w of weightHistory) {
      if (!w?.date) continue;
      const existing = perDay.get(w.date);
      if (!existing) { perDay.set(w.date, w); continue; }
      const cur = String(w.created_at || w.updated_at || "");
      const prev = String(existing.created_at || existing.updated_at || "");
      if (cur >= prev) perDay.set(w.date, w);
    }

    // Pick the LAST reading in the bucket (by date, then by created_at) — reflects real end-of-period weight
    const pickLatest = (entries) => {
      if (!entries.length) return null;
      const sorted = [...entries].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return String(a.created_at || "").localeCompare(String(b.created_at || ""));
      });
      return +sorted[sorted.length - 1].weight_kg;
    };

    if (viewMode === "week") {
      // 7 daily buckets starting at weekStart (Monday)
      return Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        const iso = toISO(d);
        const entry = perDay.get(iso);
        return {
          label: (lang === "en" ? EN_DAYS : AR_DAYS)[d.getDay() === 0 ? 6 : d.getDay() - 1],
          date: iso,
          weight: entry ? +entry.weight_kg : null,
          activeGoal: goalAt(iso),
        };
      });
    }

    if (viewMode === "month") {
      const y = monthCursor.year;
      const mo = monthCursor.month;
      const last = new Date(y, mo, 0).getDate();
      const inMonth = Array.from(perDay.values()).filter((w) => {
        const [wy, wm] = w.date.split("-").map(Number);
        return wy === y && wm === mo;
      });
      // Bucket by week-of-month (1..5)
      const buckets = new Map();
      for (const e of inMonth) {
        const dd = +e.date.split("-")[2];
        const wk = Math.min(5, Math.ceil(dd / 7));
        if (!buckets.has(wk)) buckets.set(wk, []);
        buckets.get(wk).push(e);
      }
      // Show up to 5 weeks (or fewer if month is short)
      const totalWeeks = Math.ceil(last / 7);
      return Array.from({ length: totalWeeks }).map((_, i) => {
        const wk = i + 1;
        const entries = buckets.get(wk) || [];
        const weekEnd = `${y}-${String(mo).padStart(2, "0")}-${String(Math.min(wk * 7, last)).padStart(2, "0")}`;
        return {
          label: lang === "en" ? `Week ${wk}` : `أسبوع ${wk}`,
          weekStart: `${y}-${String(mo).padStart(2, "0")}-${String((wk - 1) * 7 + 1).padStart(2, "0")}`,
          weekEnd,
          weight: pickLatest(entries),
          activeGoal: goalAt(weekEnd),
        };
      });
    }

    // Year view — 12 monthly buckets
    const buckets = new Map();
    for (const e of perDay.values()) {
      const [wy, wm] = e.date.split("-").map(Number);
      if (wy !== yearCursor) continue;
      if (!buckets.has(wm)) buckets.set(wm, []);
      buckets.get(wm).push(e);
    }
    return Array.from({ length: 12 }).map((_, i) => {
      const mo = i + 1;
      const entries = buckets.get(mo) || [];
      const lastDay = new Date(yearCursor, mo, 0).getDate();
      const monthEnd = `${yearCursor}-${String(mo).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return {
        label: String(mo),
        month: mo,
        weight: pickLatest(entries),
        activeGoal: goalAt(monthEnd),
      };
    });
  }, [weightHistory, viewMode, weekStart, monthCursor, yearCursor, user?.goal, goalAt, lang]);

  // Only points that have a reading — used for min/max/change stats
  const weightPoints = useMemo(() => weightSeries.filter((s) => s.weight != null), [weightSeries]);

  // Detect goal-change + achievement events inside the current view.
  // Goal-CHANGE events come straight from goalHistory (real dates, no bucket rounding).
  // Achievement events remain bucket-driven (they depend on when a weight was logged).
  const goalEvents = useMemo(() => {
    const events = [];

    // Determine the ISO range of the current view so we only surface relevant changes
    const viewDates = weightSeries.map((s) => s.date || s.weekEnd || s.weekStart).filter(Boolean);
    const viewStart = viewDates[0];
    const viewEnd = viewDates[viewDates.length - 1];

    const fmtLongIsoAr = (iso) => {
      if (!iso) return "";
      const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
      if (lang === "en") {
        const en = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        return `${en[m - 1]} ${d}, ${y}`;
      }
      const months = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
      return `${d} ${months[m - 1]} ${y}`;
    };

    // Goal-change events from history — one per real change, using the real date
    const sortedHistory = [...(goalHistory || [])]
      .sort((a, b) => (a.effective_from || "").localeCompare(b.effective_from || ""));
    for (const g of sortedHistory) {
      if (g.previous_target_kg == null) continue;                       // seed entry, no previous goal
      if (Math.abs(g.previous_target_kg - g.target_weight_kg) <= 0.05) continue;
      if (viewStart && g.effective_from < viewStart) continue;
      if (viewEnd && g.effective_from > viewEnd) continue;
      events.push({
        kind: "change",
        dateLabel: fmtLongIsoAr(g.effective_from),
        from: g.previous_target_kg,
        to: g.target_weight_kg,
      });
    }

    // Achievement events — bucket-based (weight-crossed-goal moments)
    for (let i = 0; i < weightSeries.length; i++) {
      const cur = weightSeries[i];
      if (cur?.weight != null && cur?.activeGoal != null &&
          Math.abs(cur.weight - cur.activeGoal) <= 0.3) {
        const iso = cur.date || cur.weekEnd || null;
        events.push({
          kind: "reached",
          dateLabel: iso ? fmtLongIsoAr(iso) : cur.label,
          goal: cur.activeGoal,
        });
      }
    }
    return events;
  }, [weightSeries, goalHistory]);

  // Persist which goal-event cards the user has dismissed so we don't nag them again.
  const DISMISS_KEY = "calofay:goal-events-dismissed";
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); }
    catch { return new Set(); }
  });
  const eventId = (ev) => ev.kind === "change"
    ? `change-${ev.dateLabel}-${ev.from}-${ev.to}`
    : `reached-${ev.dateLabel}-${ev.goal}`;
  const persistDismissed = (next) => {
    setDismissed(new Set(next));
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch {}
  };
  const dismissEvent = (id) => { const n = new Set(dismissed); n.add(id); persistDismissed(n); };
  const clearAllEvents = () => {
    const n = new Set(dismissed);
    goalEvents.forEach((ev) => n.add(eventId(ev)));
    persistDismissed(n);
  };
  const visibleGoalEvents = goalEvents.filter((ev) => !dismissed.has(eventId(ev)));

  const current = user?.current_weight_kg;
  const target = user?.target_weight_kg;
  const heightM = user?.height_cm ? user.height_cm / 100 : null;
  const bmi = current && heightM ? +(current / (heightM * heightM)).toFixed(1) : null;
  const bmiInfo = bmi ? bmiCategory(bmi, lang) : null;

  const weightDelta = current && target ? +(current - target).toFixed(1) : null;
  const isLoss = weightDelta > 0;

  // Weight display unit — user can toggle inline; persisted to backend
  const unit = user?.weight_unit || "kg";
  const uLbl = unitLabelEn(unit);
  const uLblAr = unitLabel(unit);
  const currentDisplay = kgToDisplay(current, unit);
  const targetDisplay  = kgToDisplay(target, unit);
  const deltaDisplay   = weightDelta == null ? null : Math.abs(weightDelta) * (unit === "lb" ? KG_TO_LB : 1);

  const toggleUnit = async () => {
    const next = unit === "kg" ? "lb" : "kg";
    try {
      await api.patch("/user/weight-unit", { weight_unit: next });
      await refresh?.();
      toast.success(next === "lb"
        ? (lang === "en" ? "Switched to pounds ✅" : "التحويل إلى الرطل ✅")
        : (lang === "en" ? "Switched to kilograms ✅" : "التحويل إلى الكيلوجرام ✅"));
    } catch { toast.error(lang === "en" ? "Couldn't change unit" : "تعذر تغيير الوحدة"); }
  };

  // Smart goal projection — replaces the old naive "days = delta / weekly_change_kg × 7".
  // Now considers whether the user's SET daily_calories actually create the deficit/surplus their goal needs.
  //   • Auto mode: targets are computed from goal, so always aligned → show ETA date.
  //   • Manual mode: user might have set calories that CONFLICT with their goal (e.g. wants to lose weight
  //     but set daily_calories ≥ TDEE). In that case we hide the date and show a warning note instead.
  //   • Also builds a "reality check" using the actual logged data (avg_cal_in vs TDEE) for real-world ETA.
  const goalProjection = useMemo(() => {
    if (!current || !target || weightDelta == null) return null;
    const absDelta = Math.abs(weightDelta);
    if (absDelta < 0.1) return { mode: "reached" };

    const goal = user?.goal || (weightDelta > 0 ? "lose" : weightDelta < 0 ? "gain" : "maintain");
    const tdee = Number(data?.tdee || user?.tdee || 0);
    const dailyCals = Number(user?.daily_calories || 0);
    const calcMode = user?.calc_mode || "auto";

    // Signed daily delta from target: negative = target creates a deficit, positive = surplus.
    // Only meaningful if we know both tdee and daily_calories.
    const targetDailyDelta = (tdee > 0 && dailyCals > 0) ? (dailyCals - tdee) : null;

    // Alignment check — is the user's SET target consistent with their goal direction?
    let isAligned = true;
    let conflictReason = null;
    let advice = null;
    if (targetDailyDelta != null) {
      if (goal === "lose" && targetDailyDelta > -50) {
        isAligned = false;
        conflictReason = lang === "en"
          ? `Your calories (${dailyCals}) are near or above your body's burn (${tdee})`
          : `سعراتك (${dailyCals}) قريبة من أو أعلى من حرق جسمك (${tdee})`;
        advice = lang === "en"
          ? "Reduce calories or increase activity to create a real deficit"
          : "قلّل السعرات أو زد النشاط لتحقيق عجز حقيقي";
      } else if (goal === "gain" && targetDailyDelta < 50) {
        isAligned = false;
        conflictReason = lang === "en"
          ? `Your calories (${dailyCals}) are near or below your body's burn (${tdee})`
          : `سعراتك (${dailyCals}) قريبة من أو أقل من حرق جسمك (${tdee})`;
        advice = lang === "en"
          ? "Increase calories to create a surplus that supports weight gain"
          : "زد السعرات لتحقيق فائض يدعم الزيادة";
      } else if (goal === "maintain" && Math.abs(targetDailyDelta) > 250) {
        isAligned = false;
        conflictReason = targetDailyDelta > 0
          ? (lang === "en"
            ? `Your calories (${dailyCals}) exceed your body's burn (${tdee}) by ${targetDailyDelta}`
            : `سعراتك (${dailyCals}) تفوق حرق جسمك (${tdee}) بـ ${targetDailyDelta}`)
          : (lang === "en"
            ? `Your calories (${dailyCals}) are below your body's burn (${tdee}) by ${Math.abs(targetDailyDelta)}`
            : `سعراتك (${dailyCals}) أقل من حرق جسمك (${tdee}) بـ ${Math.abs(targetDailyDelta)}`);
        advice = lang === "en"
          ? "Bring your calories closer to your body's burn to maintain weight"
          : "قرّب سعراتك من حرق جسمك للحفاظ على الوزن";
      }
    }

    // Days-to-goal ETA — prefer computing from target's actual daily delta (more honest for manual mode).
    // Falls back to user.weekly_change_kg only for maintain-goal auto-mode users.
    let days = null;
    let targetDate = null;
    if (isAligned && goal !== "maintain") {
      const dailyKgRate = targetDailyDelta != null
        ? Math.abs(targetDailyDelta) / 7700           // real biological rate from calories
        : Math.abs(user?.weekly_change_kg || 0.5) / 7; // fallback (auto mode without tdee)
      if (dailyKgRate > 0.001) {
        days = Math.ceil(absDelta / dailyKgRate);
        targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + days);
      }
    }

    return {
      mode: isAligned ? "on_track" : "conflict",
      days, targetDate, conflictReason, advice,
      calcMode, goal, tdee, dailyCals, targetDailyDelta,
    };
  }, [current, target, weightDelta, user?.goal, user?.calc_mode, user?.daily_calories,
      user?.tdee, user?.weekly_change_kg, data?.tdee]);

  const fmtArDate = (d) => {
    if (!d) return "";
    if (lang === "en") {
      const en = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return `${en[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
    const ar = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
    return `${d.getDate()} ${ar[d.getMonth()]} ${d.getFullYear()}`;
  };

  // --------------- Range navigation ---------------
  const canGoNext = useMemo(() => {
    if (viewMode === "week") {
      const nextStart = new Date(weekStart);
      nextStart.setDate(nextStart.getDate() + 7);
      return nextStart <= today;
    }
    if (viewMode === "month") {
      const nextY = monthCursor.month === 12 ? monthCursor.year + 1 : monthCursor.year;
      const nextM = monthCursor.month === 12 ? 1 : monthCursor.month + 1;
      return new Date(nextY, nextM - 1, 1) <= today;
    }
    return yearCursor < today.getFullYear();
  }, [viewMode, weekStart, monthCursor, yearCursor, today]);

  const goPrev = () => {
    if (viewMode === "week") {
      const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d);
    } else if (viewMode === "month") {
      const y = monthCursor.month === 1 ? monthCursor.year - 1 : monthCursor.year;
      const m = monthCursor.month === 1 ? 12 : monthCursor.month - 1;
      setMonthCursor({ year: y, month: m });
    } else {
      setYearCursor(y => y - 1);
    }
  };
  const goNext = () => {
    if (!canGoNext) return;
    if (viewMode === "week") {
      const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d);
    } else if (viewMode === "month") {
      const y = monthCursor.month === 12 ? monthCursor.year + 1 : monthCursor.year;
      const m = monthCursor.month === 12 ? 1 : monthCursor.month + 1;
      setMonthCursor({ year: y, month: m });
    } else {
      setYearCursor(y => y + 1);
    }
  };

  const rangeLabel = useMemo(() => {
    if (viewMode === "week") {
      const end = new Date(weekStart); end.setDate(end.getDate() + 6);
      return `${fmtDayShort(weekStart, lang)} — ${fmtDayShort(end, lang)}`;
    }
    if (viewMode === "month") {
      const months = lang === "en" ? EN_MONTHS_FULL : AR_MONTHS_FULL;
      return `${months[monthCursor.month - 1]} ${monthCursor.year}`;
    }
    return `${yearCursor}`;
  }, [viewMode, weekStart, monthCursor, yearCursor, lang]);

  const nav = useNavigate();
  const [weightModalOpen, setWeightModalOpen] = useState(() => {
    // Auto-reopen the modal ONLY if the draft is very recent (<3 minutes).
    // This targets the iOS Safari camera-intent purge: the WebView is
    // reloaded within seconds, so a fresh draft means "user was mid-flow
    // and got bounced back". An older draft means the user opened the
    // modal earlier, closed it manually, and is now just navigating to
    // Progress — we must NOT surprise them by popping the modal open.
    try {
      const d = JSON.parse(sessionStorage.getItem("calofay:weight-log-draft") || "null");
      if (!d) return false;
      const fresh = d.savedAt && (Date.now() - d.savedAt) < 3 * 60 * 1000;
      if (!fresh) {
        // Clear stale draft so it doesn't linger and confuse future opens.
        try { sessionStorage.removeItem("calofay:weight-log-draft"); } catch {}
        return false;
      }
      return !!(d.weight || d.notes || (d.measurements && Object.keys(d.measurements).length));
    } catch { return false; }
  });
  const [goalModalOpen, setGoalModalOpen] = useState(false);

  // In English mode, translate the backend-provided Arabic day/date labels
  // (اثنين/ثلاثاء/... for week view; %d/%m for month view is already numeric)
  // by mapping via the ISO date on each series row → EN_DAYS[weekday].
  const series = useMemo(() => {
    const raw = data?.series || [];
    if (lang !== "en" || viewMode === "year") return raw;
    if (viewMode === "week") {
      return raw.map((s) => {
        if (!s.date) return s;
        try {
          const dt = new Date(s.date);
          if (isNaN(dt.getTime())) return s;
          return { ...s, label: EN_DAYS[dt.getDay() === 0 ? 6 : dt.getDay() - 1] };
        } catch { return s; }
      });
    }
    return raw;
  }, [data?.series, lang, viewMode]);

  // For MONTH view: aggregate daily series into ~4-5 weekly buckets (week-of-month).
  // For WEEK view (7 days) and YEAR view (12 months) we use the raw series as-is.
  const chartSeries = useMemo(() => {
    if (viewMode !== "month") return series;
    if (!series.length) return [];
    const inclEx = !!data?.include_exercise_calories;
    const buckets = new Map();
    for (const d of series) {
      if (!d.date) continue;
      const [, , dd] = d.date.split("-").map(Number);
      const wk = Math.min(5, Math.ceil(dd / 7)); // 1..5
      if (!buckets.has(wk)) buckets.set(wk, { wk, meal_days: 0, ex_days: 0, water_days: 0, steps_days: 0,
        minDate: null, maxDate: null,
        cal_in: 0, cal_out: 0, water_ml: 0, steps: 0, protein_g: 0, carbs_g: 0, fat_g: 0,
        // Sum true per-day balances so aggregated weekly balance stays historically
        // accurate — using a single "today" TDEE would rewrite history whenever
        // the user's weight changes.
        balance_sum: 0, tdee_sum: 0 });
      const b = buckets.get(wk);
      if (!b.minDate || d.date < b.minDate) b.minDate = d.date;
      if (!b.maxDate || d.date > b.maxDate) b.maxDate = d.date;
      // Count active days per metric (only days where that metric had activity) — matches backend semantic
      if ((d.cal_in || 0) > 0)   { b.cal_in    += d.cal_in;    b.protein_g += d.protein_g || 0;
                                    b.carbs_g   += d.carbs_g   || 0; b.fat_g     += d.fat_g     || 0;
                                    b.meal_days += 1;
                                    b.balance_sum += (d.balance || 0);
                                    b.tdee_sum    += (d.tdee    || 0); }
      if ((d.cal_out || 0) > 0)  { b.cal_out   += d.cal_out;  b.ex_days    += 1; }
      if ((d.water_ml || 0) > 0) { b.water_ml  += d.water_ml; b.water_days += 1; }
      if ((d.steps || 0) > 0)    { b.steps     += d.steps;    b.steps_days += 1; }
    }
    return Array.from(buckets.values()).sort((a, b) => a.wk - b.wk).map((b) => {
      const div = (v, d) => (d > 0 ? Math.round(v / d) : 0);
      const avgIn = div(b.cal_in, b.meal_days);
      // Weekly balance = average of true per-day balances (which each used the
      // TDEE that was correct on that specific day).
      const balance = b.meal_days > 0 ? Math.round(b.balance_sum / b.meal_days) : 0;
      const avgTdee = div(b.tdee_sum, b.meal_days);
      // Bucket "contains today" if the current calendar date falls between minDate and maxDate.
      const todayIso = data?.today_iso || new Date().toISOString().slice(0, 10);
      const containsToday = b.minDate && b.maxDate && todayIso >= b.minDate && todayIso <= b.maxDate;
      return {
        label: lang === "en" ? `Week ${b.wk}` : `أسبوع ${b.wk}`,
        weekStart: b.minDate,
        weekEnd: b.maxDate,
        cal_in:    avgIn,
        cal_out:   div(b.cal_out,   b.ex_days),
        water_ml:  div(b.water_ml,  b.water_days),
        steps:     div(b.steps,     b.steps_days),
        protein_g: div(b.protein_g, b.meal_days),
        carbs_g:   div(b.carbs_g,   b.meal_days),
        fat_g:     div(b.fat_g,     b.meal_days),
        balance,
        tdee: avgTdee,
        has_meals: b.meal_days > 0,
        is_today: containsToday,
      };
    });
  }, [series, viewMode, data?.tdee, data?.include_exercise_calories, data?.today_iso, lang]);

  // Tooltip factory bound to current viewMode + year cursor
  const tipStepsProps = { viewMode, cursorYear: yearCursor, lang };

  return (
    <div className="pb-4 bg-transparent min-h-screen" dir={lang === "en" ? "ltr" : "rtl"}>
      <ShareProgressModal open={shareOpen} onClose={() => setShareOpen(false)} />
      <header className="px-5 pt-8 pb-2 flex items-center justify-between gap-3">
        <div className="text-right flex-1">
          <h1 className="text-3xl font-bold" data-testid="progress-title">
            {isSharedMode ? `${t("prog.title")}: ${user?.full_name || ""}` : t("prog.title")}
          </h1>
          <div className="text-sm text-neutral-500 mt-1">
            {isSharedMode ? "" : t("prog.weight_log")}
          </div>
        </div>
        {!isSharedMode && (
          <button
            onClick={() => setShareOpen(true)}
            className="group relative inline-flex items-center gap-2 rounded-full px-4 py-2.5 bg-gradient-to-r from-[#FF8833] to-[#FF6B00] text-white font-bold text-xs shadow-[0_6px_18px_-4px_rgba(255,107,0,0.55)] hover:shadow-[0_10px_24px_-4px_rgba(255,107,0,0.7)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-200 whitespace-nowrap"
            data-testid="progress-share-btn"
            aria-label={lang === "en" ? "Share progress with coach" : "مشاركة تقدمي مع الكوتش"}
            title={lang === "en" ? "Share progress with coach" : "شارك تقدمك مع مدربك"}
          >
            {/* subtle shine on hover — sweeps across the pill */}
            <span className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
              <span className="absolute top-0 -left-full w-1/2 h-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:left-full transition-all duration-700" />
            </span>
            <Share2 className="w-3.5 h-3.5 relative z-10" strokeWidth={2.5} />
            <span className="relative z-10">
              {lang === "en" ? "Share with coach" : "شارك مع مدربك"}
            </span>
          </button>
        )}
      </header>

      {/* Coach-only "Trainee Profile" card — shows onboarding inputs, calc-mode,
          include_exercise_calories flag, macros, adherence + safety signals so
          the coach has full context before reviewing charts. */}
      {isSharedMode && <TraineeProfileCard user={user} />}

      {/* Two side-by-side weight cards with soft orange washes */}
      <div className="px-5 mb-2 grid grid-cols-2 gap-3">
        {/* Current weight */}
        <div
          className="rounded-3xl shadow-card p-4 relative overflow-hidden bg-gradient-to-br from-[#FFF6EE] to-[#FFEEDB] dark:from-neutral-800 dark:to-neutral-900"
          data-testid="progress-current-card"
        >
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-orange-200/40 blur-2xl pointer-events-none" />
          {!isSharedMode && (
            <button
              onClick={() => setWeightModalOpen(true)}
              className={`absolute top-3 ${lang === "en" ? "right-3" : "left-3"} w-8 h-8 rounded-full bg-white/90 text-brand-500 flex items-center justify-center hover:bg-white shadow-sm z-20`}
              data-testid="progress-edit-weight-btn"
              aria-label={t("prog.weight_log")}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="relative pointer-events-none">
            <div className="text-xs text-neutral-500 mb-1 font-semibold">{t("prog.weight_current")}</div>
            <div className="text-3xl font-black num text-neutral-900" data-testid="progress-current-weight">
              {current ? current.toFixed(1) : "—"}<span className="text-sm text-neutral-400"> kg</span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-2 leading-tight">{lang === "en" ? "Update weekly to track your progress" : "قم بالتحديث أسبوعياً لتتبع تقدمك"}</div>
          </div>
        </div>
        {/* Goal weight — clickable to edit + auto recalc macros */}
        <button
          onClick={() => !isSharedMode && setGoalModalOpen(true)}
          disabled={isSharedMode}
          className={`rounded-3xl shadow-card p-4 relative overflow-hidden hover:shadow-lg transition-shadow disabled:cursor-default bg-gradient-to-br from-[#FFF6EE] to-[#FFEEDB] dark:from-neutral-800 dark:to-neutral-900 ${lang === "en" ? "text-left" : "text-right"}`}
          data-testid="progress-goal-card"
        >
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-orange-200/40 blur-2xl pointer-events-none" />
          {!isSharedMode && (
            <div className={`absolute top-3 ${lang === "en" ? "right-3" : "left-3"} w-8 h-8 rounded-full bg-white/90 text-brand-500 flex items-center justify-center shadow-sm`}>
              <Pencil className="w-3.5 h-3.5" />
            </div>
          )}
          <div className="relative pointer-events-none">
            <div className="text-xs text-neutral-500 mb-1 font-semibold">{t("prog.weight_target")}</div>
            <div className="text-3xl font-black num text-brand-500" data-testid="progress-target-weight">
              {target ? target.toFixed(1) : "—"}<span className="text-sm text-neutral-400"> kg</span>
            </div>
            <div className="text-[10px] text-neutral-500 mt-2 flex items-center gap-1 leading-tight">
              {isLoss ? <TrendingDown className="w-3 h-3 text-emerald-500" /> : (weightDelta < 0 ? <TrendingUp className="w-3 h-3 text-sky-500" /> : null)}
              {weightDelta != null ? (lang === "en" ? `${Math.abs(weightDelta).toFixed(1)} kg to go` : `المتبقي ${Math.abs(weightDelta).toFixed(1)} كجم`) : (lang === "en" ? "Tap to edit" : "اضغط للتعديل")}
            </div>
            {/* NOTE: "ستصل خلال X يوم" moved to the Reality Check card only —
                the version there is more accurate (uses actual logged intake
                + exercise), not just the planned weekly rate. Removed here
                to avoid confusing duplicate ETAs. */}
            {goalProjection?.mode === "conflict" && (
              <div className="text-[10px] text-amber-700 font-bold mt-1 leading-tight flex items-center gap-1" data-testid="progress-goal-conflict-badge">
                <span>⚠️</span>
                <span>{lang === "en" ? "Your calories don't support the goal" : "سعراتك لا تدعم الهدف"}</span>
              </div>
            )}
            {goalProjection?.mode === "reached" && (
              <div className="text-[10px] text-emerald-600 font-semibold mt-1 leading-tight" data-testid="progress-goal-reached">
                {lang === "en" ? "🎉 You reached your goal!" : "🎉 وصلت إلى هدفك!"}
              </div>
            )}
          </div>
        </button>
      </div>

      {/* ==================== مراجعة الواقع (Reality Check) ====================
          Compares the user's stated goal + set daily_calories + actual logged intake
          against their TDEE. Shows one of three states:
            1. On-track  → green "Everything aligns" block
            2. Conflict  → amber warning + specific fix advice (manual mode risk)
            3. Reality mismatch → blue block comparing target vs actual behaviour
      */}
      {goalProjection && goalProjection.mode !== "reached" && goalProjection.tdee > 0 && (() => {
        const goal = goalProjection.goal;
        const isConflict = goalProjection.mode === "conflict";
        const goalLabelAr = goal === "lose" ? "الإنقاص" : goal === "gain" ? "الزيادة" : "الحفاظ";
        const goalLabelEn = goal === "lose" ? "weight loss" : goal === "gain" ? "weight gain" : "maintenance";
        const goalLabel = lang === "en" ? goalLabelEn : goalLabelAr;

        // Actual behaviour signals — pulled from the SAME numbers as the "الفارق الحراري" chart.
        // We deliberately use the "excl_today" variants so that a partially-logged today
        // (e.g. user only ate breakfast + lunch so far) doesn't fabricate a huge fake deficit.
        const actualAvgIn = Math.round(Number(data?.avg_cal_in || 0));
        const activeDaysAll = Number(data?.active_days || 0);
        const activeDays = Number(data?.active_days_excl_today ?? activeDaysAll);
        const todayHasMeals = !!data?.today_has_meals;
        const actualDailyDelta = activeDays > 0 ? Math.round(Number(data?.avg_balance_excl_today || 0)) : null;
        const cumulative = Math.round(Number(data?.cumulative_balance_excl_today || 0));
        const estKg = Number(data?.est_weight_change_kg_excl_today || 0);

        // Edge case: user has ONLY logged today (nothing from previous days). Averages would be
        // based on an incomplete day only — show a friendly nudge instead of misleading numbers.
        const onlyTodayLogged = todayHasMeals && activeDays === 0;

        // Does the actual behaviour support the stated goal? (uses the same delta the chart shows)
        let actualSupportsGoal = null;
        if (actualDailyDelta != null) {
          if (goal === "lose")     actualSupportsGoal = actualDailyDelta < -100;
          else if (goal === "gain") actualSupportsGoal = actualDailyDelta > +100;
          else                      actualSupportsGoal = Math.abs(actualDailyDelta) <= 250;
        }

        // Weekly projection — for "week" view use raw estKg; for month/year normalise back to weekly rate.
        const weeklyKgAbs = activeDays > 0
          ? Math.abs((cumulative / activeDays) * 7 / 7700)   // avg-daily-balance × 7 ÷ 7700
          : 0;
        const dirWord = actualDailyDelta == null ? "" :
          actualDailyDelta < -50 ? "نزول" :
          actualDailyDelta > 50  ? "زيادة" : "ثبات";

        return (
          <div className="px-5 mb-4" data-testid="reality-check-strip">
            <div className={`rounded-3xl shadow-card p-4 space-y-3 ${isConflict ? "bg-amber-50" : "bg-white"}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold text-neutral-500 tracking-wide">{lang === "en" ? "Reality Check" : "مراجعة الواقع"}</div>
                <div className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${isConflict ? "bg-amber-200 text-amber-900" : "bg-emerald-100 text-emerald-700"}`}>
                  {isConflict ? (lang === "en" ? "⚠️ Needs review" : "⚠️ يحتاج مراجعة") : (lang === "en" ? "✅ On track" : "✅ في الطريق الصحيح")}
                </div>
              </div>

              {/* Row 1: Target-vs-goal alignment */}
              {isConflict ? (
                <div className="rounded-2xl bg-white border border-amber-200 p-3" data-testid="reality-conflict">
                  <div className="text-[12px] font-bold text-amber-800 mb-1">
                    {lang === "en"
                      ? `Your goal: ${goalLabel} · but your calories don't support it`
                      : `هدفك: ${goalLabelAr} · لكن سعراتك لا تدعم ذلك`}
                  </div>
                  <div className="text-[11px] text-amber-700 leading-relaxed">
                    {goalProjection.conflictReason}
                  </div>
                  <div className="text-[11px] text-neutral-600 leading-relaxed mt-1.5">
                    💡 {goalProjection.advice}
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3" data-testid="reality-on-track">
                  <div className="text-[12px] font-bold text-emerald-800">
                    {lang === "en"
                      ? `✅ Your daily calories (${goalProjection.dailyCals}) support your ${goalLabelEn} goal`
                      : `✅ سعراتك اليومية (${goalProjection.dailyCals}) تدعم هدف ${goalLabelAr}`}
                  </div>
                  {goalProjection.days != null && (() => {
                    // ETA transparency: "days" is the PLAN projection (target
                    // deficit ÷ 7700). But the user's ACTUAL month trend may
                    // differ. Show both when we have ≥3 completed days —
                    // beginners get an honest picture instead of a fictional plan-only ETA.
                    const absDelta = Math.abs(weightDelta || 0);
                    const monthKg = Math.abs(trendAll?.month?.est_kg || 0);
                    const monthDays = Number(trendAll?.month?.active_days || 0);
                    let actualEtaDays = null;
                    let actualEtaDate = null;
                    if (monthDays >= 3 && monthKg > 0.05 && absDelta > 0.1) {
                      const dailyKg = monthKg / monthDays; // kg per completed day
                      if (dailyKg > 0.005) {
                        actualEtaDays = Math.ceil(absDelta / dailyKg);
                        actualEtaDate = new Date();
                        actualEtaDate.setDate(actualEtaDate.getDate() + actualEtaDays);
                      }
                    }
                    return (
                      <>
                        <div className="text-[11px] text-emerald-700 mt-1">
                          <span className="text-neutral-500">{lang === "en" ? "Current plan:" : "بالخطة الحالية:"}</span>{" "}
                          <span className="num font-black">{goalProjection.days}</span> {lang === "en" ? "days" : "يوم"}
                          <span className="text-neutral-500 mr-1 num">({fmtArDate(goalProjection.targetDate)})</span>
                        </div>
                        {actualEtaDays != null && (
                          <div className="text-[11px] mt-0.5" data-testid="reality-eta-actual">
                            <span className="text-neutral-500">{lang === "en" ? "Actual pace:" : "بالمعدل الفعلي:"}</span>{" "}
                            <span className={`num font-black ${actualEtaDays < goalProjection.days ? "text-emerald-700" : "text-orange-700"}`}>
                              {actualEtaDays}
                            </span>{" "}
                            <span className={actualEtaDays < goalProjection.days ? "text-emerald-700" : "text-orange-700"}>{lang === "en" ? "days" : "يوم"}</span>
                            <span className="text-neutral-500 mr-1 num">({fmtArDate(actualEtaDate)})</span>
                            {actualEtaDays < goalProjection.days ? (
                              <span className="text-[9px] text-emerald-600 mr-1">{lang === "en" ? "⚡ Ahead of plan" : "⚡ أسرع من الخطة"}</span>
                            ) : actualEtaDays > goalProjection.days * 1.3 ? (
                              <span className="text-[9px] text-orange-600 mr-1">{lang === "en" ? "🐢 Behind plan" : "🐢 أبطأ من الخطة"}</span>
                            ) : null}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Row 2 removed: the 3-column compare table (week/month/year kg)
                  duplicated the daily balance chart below. The ETA lines in
                  Row 1 above provide the unique summary the user actually needs. */}
            </div>
          </div>
        );
      })()}

      {/* Studio (photo gallery) shortcut + recent photos preview */}
      <div className="px-5 mb-4">
        <div className="bg-white rounded-3xl shadow-card overflow-hidden">
          <button
            onClick={() => {
              // In shared mode, route to the parallel /share/progress/:token/photos page
              // (read-only mirror). The token lives in the URL of the current mount.
              if (isSharedMode) {
                const m = window.location.pathname.match(/^\/share\/progress\/([^/]+)/);
                if (m) nav(`/share/progress/${m[1]}/photos`);
              } else {
                nav("/app/progress/photos");
              }
            }}
            className="w-full p-3 flex items-center justify-between hover:bg-neutral-50 disabled:cursor-default"
            data-testid="progress-studio-btn"
          >
            <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center">
              <Images className="w-4 h-4 text-brand-500" />
            </div>
            <div className="flex-1 text-right mr-3">
              <div className="text-sm font-semibold text-neutral-900">
                {lang === "en" ? "Photo & Measurement Studio" : "استوديو الصور والقياسات"}
              </div>
              <div className="text-[11px] text-neutral-500">
                {lang === "en"
                  ? (isSharedMode ? "A visual glimpse of your progress — view and compare" : "See your progress over time — sorted by date")
                  : (isSharedMode ? "لمحة مرئية عن رحلة التقدم — عرض ومقارنة" : "اعرض تقدمك زمنياً — مرتّبة حسب التاريخ")}
              </div>
            </div>
          </button>
          {recentPhotos.length > 0 ? (
            <div className="px-3 pb-3 pt-1">
              <div className="flex gap-2 overflow-x-auto no-scrollbar" dir="rtl" data-testid="progress-recent-photos">
                {recentPhotos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      if (isSharedMode) {
                        const m = window.location.pathname.match(/^\/share\/progress\/([^/]+)/);
                        if (m) nav(`/share/progress/${m[1]}/photos`);
                      } else {
                        nav("/app/progress/photos");
                      }
                    }}
                    className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-white ring-1 ring-neutral-200 relative"
                    data-testid={`recent-photo-${p.id}`}
                  >
                    <img
                      src={p.url || `/api/files/${p.storage_path}`}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {p.date && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-[9px] text-white text-center py-0.5 num">
                        {p.date.slice(5).replace("-", "/")}
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  if (isSharedMode) {
                    const m = window.location.pathname.match(/^\/share\/progress\/([^/]+)/);
                    if (m) nav(`/share/progress/${m[1]}/photos`);
                  } else {
                    nav("/app/progress/photos");
                  }
                }}
                className="w-full mt-2 text-center text-xs font-semibold text-brand-600 hover:text-brand-700 py-1"
                data-testid="progress-view-all-photos"
              >
                {lang === "en" ? "View all photos →" : "عرض جميع الصور →"}
              </button>
            </div>
          ) : isSharedMode ? (
            <div className="p-4 text-center text-[11px] text-neutral-400" data-testid="progress-shared-no-photos">
              {lang === "en" ? "No shared photos" : "لا توجد صور مشتركة"}
            </div>
          ) : (
            <button
              onClick={() => setWeightModalOpen(true)}
              className="w-full mx-3 mb-3 rounded-2xl border-2 border-dashed border-orange-200 hover:border-brand-500 bg-orange-50/40 hover:bg-orange-50 transition p-4 flex flex-col items-center justify-center gap-2 text-center"
              style={{ width: "calc(100% - 1.5rem)" }}
              data-testid="progress-empty-photos"
            >
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-2xl bg-white shadow-sm rotate-[-8deg]" />
                <div className="absolute inset-0 rounded-2xl bg-orange-100 shadow-sm rotate-[6deg]" />
                <div className="absolute inset-0 rounded-2xl bg-white shadow flex items-center justify-center border border-orange-100">
                  <ImagePlus className="w-7 h-7 text-brand-500" />
                </div>
              </div>
              <div>
                <div className="text-sm font-bold text-neutral-900">{lang === "en" ? "No photos yet" : "لا توجد صور بعد"}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{lang === "en" ? "Add your first photo to start your visual progress journey" : "أضِف صورة أولى لبدء رحلة التقدم البصري"}</div>
              </div>
              <div className="text-xs font-semibold text-brand-500 mt-1">{lang === "en" ? "+ Add photo now" : "+ أضف صورة الآن"}</div>
            </button>
          )}
        </div>
      </div>

      {/* BMI card */}
      <div className="px-5 mb-4">
        <div className="bg-white rounded-3xl shadow-card p-5" data-testid="progress-bmi-card" dir="rtl">
          <div className="flex items-center justify-between mb-3">
            <div className="text-end">
              <div className="text-sm font-semibold text-neutral-900">{lang === "en" ? "Body Mass Index (BMI)" : "مؤشر كتلة الجسم (BMI)"}</div>
              <div className="text-xs" style={{ color: bmiInfo?.color || "#78706A" }}>{bmiInfo?.l || "—"}</div>
            </div>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${bmiInfo?.color || "#78706A"}22` }}>
              <Activity className="w-5 h-5" style={{ color: bmiInfo?.color || "#78706A" }} />
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold num" style={{ color: bmiInfo?.color || "#78706A" }} data-testid="progress-bmi-value">
              {bmi ?? "—"}
            </div>
          </div>
          <BmiScale bmi={bmi} />
        </div>
      </div>

      {/* View-mode selector */}
      <div className="px-5 mb-3">
        <div className="bg-white rounded-full p-1.5 flex gap-1 shadow-card" data-testid="progress-view-mode">
          {VIEW_MODES.map((r) => (
            <button
              key={r.v}
              onClick={() => setViewMode(r.v)}
              className={`flex-1 rounded-full py-2.5 px-3 font-semibold text-xs transition-all whitespace-nowrap ${
                viewMode === r.v ? "bg-brand-500 text-white shadow" : "text-neutral-500"
              }`}
              data-testid={`view-${r.v}`}
            >
              {t(r.tKey, r.l)}
            </button>
          ))}
        </div>
      </div>

      {/* Period navigator (arrows + current label) */}
      <div className="px-5 mb-4">
        <div className="bg-white rounded-2xl p-2 flex items-center justify-between shadow-card" dir="rtl" data-testid="progress-period-nav">
          <button
            onClick={goPrev}
            className="w-12 h-12 rounded-2xl bg-neutral-100 hover:bg-brand-50 hover:text-brand-500 flex items-center justify-center text-neutral-700 transition-colors"
            data-testid="period-prev-btn"
            aria-label={lang === "en" ? "Previous" : "السابق"}
          >
            <ChevronRight className="w-7 h-7" strokeWidth={2.5} />
          </button>
          <div className="text-center flex-1 px-2" data-testid="period-label">
            <div className="text-sm font-bold text-neutral-900 num">{rangeLabel}</div>
            <div className="text-[10px] text-neutral-400 mt-0.5">
              {viewMode === "week" && (lang === "en" ? "Use arrows to navigate weeks" : "استخدم الأسهم للتنقّل بين الأسابيع")}
              {viewMode === "month" && (lang === "en" ? "Use arrows to navigate months" : "استخدم الأسهم للتنقّل بين الأشهر")}
              {viewMode === "year" && (lang === "en" ? "Use arrows to navigate years" : "استخدم الأسهم للتنقّل بين السنوات")}
            </div>
          </div>
          <button
            onClick={goNext}
            disabled={!canGoNext}
            className="w-12 h-12 rounded-2xl bg-neutral-100 hover:bg-brand-50 hover:text-brand-500 flex items-center justify-center text-neutral-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-neutral-100 disabled:hover:text-neutral-700"
            data-testid="period-next-btn"
            aria-label={lang === "en" ? "Next" : "التالي"}
          >
            <ChevronLeft className="w-7 h-7" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Chart cards */}
      <div className="px-5 space-y-4">
        {/* WEIGHT TREND — matches other charts: buckets per viewMode + value shown on each point */}
        <ChartCard
          title={lang === "en" ? "Weight trend (kg)" : "تطوّر الوزن (كجم)"}
          subtitle={weightPoints.length
            ? (viewMode === "year"
                ? (lang === "en" ? `${weightPoints.length} months logged` : `${weightPoints.length} شهر مسجّل`)
              : viewMode === "month"
                ? (lang === "en" ? `${weightPoints.length} weeks logged` : `${weightPoints.length} أسبوع مسجّل`)
              : (lang === "en" ? `${weightPoints.length} days logged` : `${weightPoints.length} يوم مسجّل`))
            : (lang === "en" ? "No measurements in this period" : "لا توجد قياسات في هذه الفترة")}
          testid="chart-weight-trend"
        >
          {weightPoints.length === 0 ? (
            <div className="text-center py-8 text-neutral-400 text-sm">
              {lang === "en"
                ? "Log your weight using the pencil button above to start the trend line"
                : "سجّل وزنك من زر القلم في الأعلى ليبدأ ظهور الخط"}
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={210}>
                <LineChart data={weightSeries} margin={{ top: 24, right: 22, left: 38, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke="#F3F4F6" />
                  <XAxis
                    dataKey="label"
                    fontSize={viewMode === "week" ? 11 : 10}
                    tick={{ fill: "#78706A", fontWeight: 600 }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    reversed={lang !== "en"}
                    height={22}
                  />
                  <YAxis
                    domain={([dataMin, dataMax]) => {
                      const targetW = Number(user?.target_weight_kg) || null;
                      const values = [...weightPoints.map((p) => p.weight), ...(targetW ? [targetW] : [])];
                      if (!values.length) return [dataMin, dataMax];
                      const lo = Math.floor(Math.min(...values) - 1);
                      const hi = Math.ceil(Math.max(...values) + 1);
                      return [lo, hi];
                    }}
                    hide
                  />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0]?.payload || {};
                    let title;
                    if (viewMode === "week") title = `${p.label} · ${fmtIsoAr(p.date, lang)}`;
                    else if (viewMode === "month") title = `${p.label} · ${fmtIsoAr(p.weekStart, lang)} — ${fmtIsoAr(p.weekEnd, lang)}`;
                    else title = (lang === "en" ? `${AR_MONTHS_TIP[p.month - 1]} ${yearCursor}` : `شهر ${AR_MONTHS_TIP[p.month - 1]} ${yearCursor}`);
                    return (
                      <div className="bg-white rounded-xl shadow-lg px-3 py-2 text-xs" dir={lang === "en" ? "ltr" : "rtl"}>
                        <div className="font-bold text-neutral-900 mb-1">{title}</div>
                        <div className="flex items-center gap-2 justify-between">
                          <span className="text-neutral-500">{lang === "en" ? "Weight" : "الوزن"}</span>
                          <span className="num font-bold text-orange-500">{(p.weight || 0).toFixed(1)} {lang === "en" ? unitLabelEn(unit) : unitLabel(unit)}</span>
                        </div>
                        {p.activeGoal != null && (
                          <div className="flex items-center gap-2 justify-between pt-1 border-t border-neutral-100 mt-1">
                            <span className="text-neutral-500">
                              {user?.target_weight_kg && Math.abs(p.activeGoal - Number(user.target_weight_kg)) > 0.05
                                ? (lang === "en" ? "Goal then" : "الهدف وقتها")
                                : (lang === "en" ? "Goal" : "الهدف")}
                            </span>
                            <span className="num font-bold text-emerald-500">
                              {(unit === "lb" ? p.activeGoal * KG_TO_LB : p.activeGoal).toFixed(1)} {lang === "en" ? unitLabelEn(unit) : unitLabel(unit)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }} />
                  {/* Static reference line at the CURRENT target weight.
                      A historical "stepped goal" line proved confusing (two dashed
                      segments jumping at each goal edit), so we now show a SINGLE
                      horizontal line at today's target only — with an inline 🎯
                      label so the user instantly reads "this is where I'm heading". */}
                  {user?.target_weight_kg && (
                    <ReferenceLine
                      y={Number(user.target_weight_kg)}
                      stroke="#22C55E"
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      ifOverflow="extendDomain"
                      label={({ viewBox }) => {
                        // Render the target number in the extra 38-px left margin
                        // so the digits sit fully OUTSIDE the plot area — never
                        // stacking above the leftmost day label (e.g. "أحد").
                        // Convert to whichever unit the user is viewing in so
                        // kg-lb switches don't leave a stale "72.0 كجم" behind.
                        const u = user?.weight_unit || "kg";
                        const raw = Number(user.target_weight_kg);
                        const shown = (u === "lb" ? raw * KG_TO_LB : raw).toFixed(1);
                        const x = viewBox?.x ?? 0;
                        const y = viewBox?.y ?? 0;
                        return (
                          <g>
                            <rect x={x - 36} y={y - 8} width={34} height={16} rx={8}
                                  fill="#DCFCE7" stroke="#22C55E" strokeWidth={1} />
                            <text
                              x={x - 19}
                              y={y + 3}
                              textAnchor="middle"
                              fontSize={9}
                              fontWeight={800}
                              fill="#166534"
                            >
                              {shown}
                            </text>
                          </g>
                        );
                      }}
                    />
                  )}
                  {/* Actual weight trend */}
                  <Line
                    type="monotone"
                    dataKey="weight"
                    name="الوزن"
                    stroke="#F97316"
                    strokeWidth={2.5}
                    connectNulls
                    dot={{ r: 4, fill: "#F97316", strokeWidth: 2, stroke: "#fff" }}
                    activeDot={{ r: 6, fill: "#F97316", strokeWidth: 2, stroke: "#fff" }}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="weight"
                      position="top"
                      formatter={(v) => (v ? v.toFixed(1) : "")}
                      fill="#F97316"
                      fontSize={10}
                      fontWeight={700}
                      offset={8}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>

              {/* ── Details toggle — mirrors the "الفارق الحراري" chart. All
                  the extra context (helper text, goal legend badge, recent
                  goal-change events, historical goal timeline) is tucked
                  behind this button so the chart itself stays clean. */}
              <button
                onClick={() => setWeightDetailsOpen((v) => !v)}
                className="mt-3 w-full flex items-center justify-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-700 font-semibold py-1.5 rounded-lg hover:bg-neutral-50 transition-colors"
                data-testid="weight-details-toggle"
                aria-expanded={weightDetailsOpen}
              >
                <span>{weightDetailsOpen ? (lang === "en" ? "Hide details" : "إخفاء التفاصيل") : (lang === "en" ? "More details" : "المزيد من التفاصيل")}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${weightDetailsOpen ? "rotate-180" : ""}`} />
              </button>

              {weightDetailsOpen && (
              <>
              <div className="mt-2 flex items-center justify-between text-[10px] px-1" dir="rtl">
                <div className="text-neutral-400 flex-1">
                  {lang === "en"
                    ? "Each point = last measurement in that period · tap any point for details"
                    : "كل نقطة = آخر قياس مسجّل في تلك الفترة · اضغط على أي نقطة لرؤية التفاصيل"}
                </div>
                {user?.target_weight_kg && (
                  <div className="flex items-center gap-1.5 flex-shrink-0 mr-2 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1"
                       data-testid="weight-goal-legend">
                    <span className="inline-block w-4 border-t-2 border-dashed border-emerald-500"></span>
                    <span className="text-emerald-800 font-bold">
                      🎯 {lang === "en" ? "Goal" : "هدفك"} <span className="num">{Number(user.target_weight_kg).toFixed(1)}</span> {lang === "en" ? "kg" : "كجم"}
                    </span>
                  </div>
                )}
              </div>
              {visibleGoalEvents.length > 0 && (
                <div className="mt-3 space-y-1.5" dir="rtl" data-testid="goal-events">
                  {visibleGoalEvents.length > 1 && (
                    <div className="flex items-center justify-between text-[10px] text-neutral-500 px-1">
                      <button
                        onClick={clearAllEvents}
                        className="font-semibold text-neutral-400 hover:text-neutral-700"
                        data-testid="goal-events-clear-all"
                      >
                        {lang === "en" ? "Dismiss all ✕" : "إخفاء الكل ✕"}
                      </button>
                      <span>{lang === "en" ? "Recent events" : "الأحداث الأخيرة"}</span>
                    </div>
                  )}
                  {visibleGoalEvents.map((ev, i) => {
                    const id = eventId(ev);
                    return ev.kind === "change" ? (
                      <div key={id} className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2" data-testid={`goal-event-${i}`}>
                        <button
                          onClick={() => dismissEvent(id)}
                          className="w-5 h-5 rounded-full bg-white/70 text-neutral-500 hover:bg-white hover:text-neutral-800 flex items-center justify-center flex-shrink-0"
                          data-testid={`goal-event-dismiss-${i}`}
                          aria-label={lang === "en" ? "Dismiss" : "إخفاء"}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="text-base">🎯</span>
                        <span className="text-neutral-700 flex-1">
                          {lang === "en" ? (
                            <>Goal changed on <span className="font-bold">{ev.dateLabel}</span> from
                              <span className="num font-bold text-neutral-500 line-through mx-1">{ev.from.toFixed(1)}</span>
                              to
                              <span className="num font-bold text-emerald-600 mx-1">{ev.to.toFixed(1)}</span>
                              kg</>
                          ) : (
                            <>تغيّر الهدف بتاريخ <span className="font-bold">{ev.dateLabel}</span> من
                              <span className="num font-bold text-neutral-500 line-through mx-1">{ev.from.toFixed(1)}</span>
                              إلى
                              <span className="num font-bold text-emerald-600 mx-1">{ev.to.toFixed(1)}</span>
                              كجم</>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div key={id} className="flex items-center gap-2 text-[11px] bg-brand-50 border border-brand-100 rounded-xl px-3 py-2" data-testid={`goal-event-${i}`}>
                        <button
                          onClick={() => dismissEvent(id)}
                          className="w-5 h-5 rounded-full bg-white/70 text-neutral-500 hover:bg-white hover:text-neutral-800 flex items-center justify-center flex-shrink-0"
                          data-testid={`goal-event-dismiss-${i}`}
                          aria-label={lang === "en" ? "Dismiss" : "إخفاء"}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="text-base">✓</span>
                        <span className="text-neutral-700 flex-1">
                          {lang === "en" ? (
                            <>Reached goal of
                              <span className="num font-bold text-brand-600 mx-1">{ev.goal.toFixed(1)}</span>
                              kg on <span className="font-bold">{ev.dateLabel}</span></>
                          ) : (
                            <>وصلت لهدف
                              <span className="num font-bold text-brand-600 mx-1">{ev.goal.toFixed(1)}</span>
                              كجم بتاريخ <span className="font-bold">{ev.dateLabel}</span></>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <AxisHint viewMode={viewMode} />
              <GoalTimeline goalHistory={goalHistory} currentGoal={user?.target_weight_kg} />
              </>
              )}
            </>
          )}
        </ChartCard>

        {/* CALORIES IN — stacked macros w/ gram labels + per-day totals */}
        <ChartCard title={lang === "en" ? "Calories consumed" : "السعرات الحرارية الداخلة"} subtitle={lang === "en" ? "(Protein / Carbs / Fat)" : "(البروتين / الكربوهيدرات / الدهون)"} testid="chart-cal-in">
          <div className="relative">
            <ResponsiveContainer width="100%" height={viewMode === "week" ? 220 : 240}>
              <BarChart
                data={chartSeries}
                margin={{ top: 24, right: 22, left: 22, bottom: 4 }}
                barCategoryGap={viewMode === "week" ? "20%" : "18%"}
              >
                <CartesianGrid vertical={false} stroke="#F3F4F6" />
                <XAxis
                  dataKey="label"
                  fontSize={viewMode === "week" ? 11 : 10}
                  tick={{ fill: "#78706A", fontWeight: 600 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  reversed={lang !== "en"}
                  height={22}
                />
                <YAxis hide domain={[0, (dataMax) => Math.max(50, Math.ceil(dataMax * 1.15))]} />
                <Tooltip cursor={false} content={makeMacroTooltip({ ...tipStepsProps })} />
                <Bar dataKey="protein_g" stackId="a" fill="#3B82F6" activeBar={false} isAnimationActive={false}>
                  <LabelList dataKey="protein_g" position="center"
                    formatter={(v) => (v > 0 ? `${Math.round(v)}g` : "")}
                    fill="#fff" fontSize={viewMode === "year" ? 6 : 8} fontWeight={700} />
                </Bar>
                <Bar dataKey="carbs_g" stackId="a" fill="#22C55E" activeBar={false} isAnimationActive={false}>
                  <LabelList dataKey="carbs_g" position="center"
                    formatter={(v) => (v > 0 ? `${Math.round(v)}g` : "")}
                    fill="#fff" fontSize={viewMode === "year" ? 6 : 8} fontWeight={700} />
                </Bar>
                <Bar dataKey="fat_g" stackId="a" fill="#F97316" radius={[10, 10, 0, 0]} activeBar={false} isAnimationActive={false}>
                  <LabelList dataKey="fat_g" position="center"
                    formatter={(v) => (v > 0 ? `${Math.round(v)}g` : "")}
                    fill="#fff" fontSize={viewMode === "year" ? 6 : 8} fontWeight={700} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Total kcal row aligned below each bar — always visible in every view */}
            <BarValueRow series={chartSeries} valueKey="cal_in" color="#F97316" testid="chart-cal-in-totals" tiny={viewMode === "year"} />          </div>
          <Legend items={[{c:"#3B82F6",l:lang === "en" ? "Protein" : "بروتين"},{c:"#22C55E",l:lang === "en" ? "Carbs" : "كارب"},{c:"#F97316",l:lang === "en" ? "Fat" : "دهون"}]} />
          <AvgRow label={lang === "en" ? (viewMode === "year" ? "Avg calories / month" : viewMode === "month" ? "Avg calories / week" : "Avg daily calories consumed") : (viewMode === "year" ? "متوسط السعرات في الشهر" : viewMode === "month" ? "متوسط السعرات في الأسبوع" : "متوسط السعرات المتناولة يومياً")} value={data?.avg_cal_in} color="#F97316" />
        </ChartCard>

        <ChartCard title={lang === "en" ? "Calories burned" : "السعرات الحرارية الخارجة"} subtitle={lang === "en" ? "(Workouts)" : "(التمارين)"} testid="chart-cal-out">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={chartSeries} margin={{ top: 10, right: 22, left: 22, bottom: 4 }}>
              <CartesianGrid vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="label" fontSize={viewMode === "week" ? 11 : 10} tick={{ fill: "#78706A", fontWeight: 600 }} axisLine={false} tickLine={false} reversed={lang !== "en"} interval={0} height={22} />
              <YAxis hide />
              <Tooltip cursor={false} content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload || {};
                const items = Array.isArray(p.exercises) ? p.exercises : [];
                const total = Math.round(p.cal_out || 0);
                const title = chartTooltipTitle(p, viewMode, yearCursor, lang);
                return (
                  <div className="bg-white/98 backdrop-blur rounded-2xl shadow-xl border border-neutral-100 p-3 min-w-[220px] max-w-[280px]" dir="rtl">
                    <div className="text-[11px] font-bold text-neutral-800 mb-2">{title}</div>
                    {items.length === 0 ? (
                      <div className="text-[11px] text-neutral-500 text-center py-1">
                        {total > 0
                          ? (lang === "en" ? `${total} cal (no details)` : `${total} سعرة (بدون تفاصيل)`)
                          : (lang === "en" ? "No workouts today" : "لا توجد تمارين هذا اليوم")}
                      </div>
                    ) : (
                      <div className="space-y-1.5" data-testid="exercise-breakdown">
                        {items.map((it, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-neutral-800 truncate">{it.name}</div>
                              {it.duration_min && (
                                <div className="text-[9px] text-neutral-500 num">{it.duration_min} {lang === "en" ? "min" : "دقيقة"}</div>
                              )}
                            </div>
                            <div className="num font-black text-red-500 flex-shrink-0" dir="ltr">
                              −{it.calories_burned}
                            </div>
                          </div>
                        ))}
                        <div className="border-t border-neutral-200 my-1"></div>
                        <div className="flex items-center justify-between text-[12px]">
                          <span className="font-bold text-neutral-800">{lang === "en" ? "Total" : "الإجمالي"}</span>
                          <span className="num font-black text-red-600" dir="ltr">−{total} {lang === "en" ? "cal" : "سعرة"}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }} />
              <Bar dataKey="cal_out" name="التمارين" fill="#F87171" radius={[10, 10, 0, 0]} activeBar={false} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
          <BarValueRow series={chartSeries} valueKey="cal_out" color="#EF4444" testid="chart-cal-out-vals" tiny={viewMode === "year"} />
          <AvgRow label={lang === "en" ? "Avg calories burned" : "متوسط السعرات المحروقة"} value={data?.avg_cal_out} color="#EF4444" />
        </ChartCard>

        {/* ============ الفارق الحراري (Calorie Balance) ============
            NEW FORMULA (iter35): balance = cal_in − TDEE − exercise_burn
            • TDEE = Total Daily Energy Expenditure (BMR × activity multiplier), stored per user.
            • Exercise is subtracted only when the user does NOT choose to "include exercise
              calories in target" (otherwise it's already earned back and would be double-counted).
            • Days without meals logged are excluded from all totals/averages so a skipped day
              doesn't fabricate a huge unrealistic deficit.
            Colours + shaded zones flip based on goal (lose → deficit is green; gain → surplus is green).
        */}
        {(() => {
          const goal = user?.goal || "lose";
          const tdee = Number(data?.tdee || 0);
          const inclEx = !!data?.include_exercise_calories;
          const todayHasMeals = !!data?.today_has_meals;
          // Use "excl_today" variants for averages/cumulative so a partially-logged
          // today doesn't fabricate a huge fake deficit in the summary numbers.
          const avg        = Math.round(data?.avg_balance_excl_today ?? data?.avg_balance ?? 0);
          const cumulative = Math.round(data?.cumulative_balance_excl_today ?? data?.cumulative_balance ?? 0);
          const estKg      = Number(data?.est_weight_change_kg_excl_today ?? data?.est_weight_change_kg ?? 0);
          const activeDays = Number(data?.active_days_excl_today ?? data?.active_days ?? 0);
          const totalDays  = Number(data?.total_days || 0);
          const skippedDays = Math.max(0, totalDays - Number(data?.active_days || 0) - (todayHasMeals ? 0 : 0));

          // Buckets — only visible chart points (has_meals) count as deficit/surplus, and exclude today (incomplete)
          const shownPoints = chartSeries.filter((s) => s.has_meals !== false && !s.is_today && (s.balance || 0) !== 0);
          const deficitDays  = shownPoints.filter((s) => s.balance < 0).length;
          const surplusDays  = shownPoints.filter((s) => s.balance > 0).length;
          const balancedDays = chartSeries.filter((s) => s.has_meals !== false && !s.is_today && Math.abs(s.balance || 0) <= 50).length;

          const goodColor    = "#10B981";  // emerald
          const badColor     = "#EF4444";  // red
          const neutralColor = "#F59E0B";  // amber

          // Colour resolver — negative = deficit, positive = surplus
          const colorForBalance = (val) => {
            if (goal === "lose") return val < 0 ? goodColor : val > 0 ? badColor : neutralColor;
            if (goal === "gain") return val > 0 ? goodColor : val < 0 ? badColor : neutralColor;
            /* maintain */       return Math.abs(val) <= 200 ? goodColor : neutralColor;
          };

          const statusLabel = (() => {
            if (goal === "lose") {
              if (avg < -100) return { l: lang === "en" ? "Healthy deficit — supports weight loss" : "عجز صحي — يدعم إنقاص الوزن", c: goodColor };
              if (avg > 100)  return { l: lang === "en" ? "Surplus — works against weight loss" : "فائض — يعاكس هدف الإنقاص",   c: badColor };
              return { l: lang === "en" ? "Roughly balanced" : "متوازن تقريباً",                              c: neutralColor };
            }
            if (goal === "gain") {
              if (avg > 100)  return { l: lang === "en" ? "Healthy surplus — supports weight gain" : "فائض صحي — يدعم زيادة الوزن", c: goodColor };
              if (avg < -100) return { l: lang === "en" ? "Deficit — works against weight gain" : "عجز — يعاكس هدف الزيادة",     c: badColor };
              return { l: lang === "en" ? "Roughly balanced" : "متوازن تقريباً",                              c: neutralColor };
            }
            if (Math.abs(avg) <= 150) return { l: lang === "en" ? "Excellent — close to maintenance" : "ممتاز — قريب من الحفاظ", c: goodColor };
            return { l: lang === "en" ? "Far from balance" : "بعيد عن التوازن",                                c: neutralColor };
          })();

          const tipCopy = goal === "lose"
            ? (lang === "en" ? "Safe weight-loss target: −300 to −700 cal daily" : "المستهدف الآمن للإنقاص: −300 إلى −700 سعرة يومياً")
            : goal === "gain"
              ? (lang === "en" ? "Safe weight-gain target: +200 to +500 cal daily" : "المستهدف الآمن للزيادة: +200 إلى +500 سعرة يومياً")
              : (lang === "en" ? "Aim for a value near zero to maintain your weight" : "استهدف قيمة قريبة من الصفر للحفاظ على وزنك");

          const subtitle = goal === "lose"
            ? (lang === "en" ? "Intake − body's natural burn − workouts" : "المتناول − حرق الجسم الطبيعي − التمارين")
            : goal === "gain"
              ? (lang === "en" ? "Intake − body's natural burn − workouts" : "المتناول − حرق الجسم الطبيعي − التمارين")
              : (lang === "en" ? "Closer to zero means better maintenance" : "الأقرب للصفر يعني حفاظاً أفضل على وزنك");

          const periodLabelAr =
            viewMode === "week"  ? (lang === "en" ? "week" : "الأسبوع")
            : viewMode === "month" ? (lang === "en" ? "month" : "الشهر")
            : (lang === "en" ? "year" : "السنة");

          // Colour for the cumulative-balance chip in the details view.
          const cumIsGood = goal === "lose" ? cumulative < 0
                          : goal === "gain" ? cumulative > 0
                          : Math.abs(cumulative) < 500;
          const cumColor  = cumulative === 0 ? neutralColor : (cumIsGood ? goodColor : badColor);

          // ── FIXED PREDICTION MATH ─────────────────────────────────────────
          // OLD (misleading): cumulative_balance / 7700 → tiny 0.09 kg if only 1 day was logged
          // NEW (motivating): projected weekly rate = avg_daily_balance × 7 / 7700
          //                    = "at your current pace, you'll lose X kg per week"
          // This uses AVERAGE (not cumulative) so even 1 logged day gives an honest projection.
          const weeklyProjKg = avg && tdee > 0 ? (avg * 7) / 7700 : 0;
          const weeklyProjKgAbs = Math.abs(weeklyProjKg);
          const weeklyGoodDir =
            goal === "lose" ? avg < -50 :
            goal === "gain" ? avg > 50 :
            Math.abs(avg) <= 200;
          const weeklyColor = activeDays === 0 ? neutralColor : weeklyGoodDir ? goodColor : cumulative === 0 ? neutralColor : badColor;
          const weeklyDirWord = weeklyProjKg < -0.02
            ? (lang === "en" ? "loss" : "نزول")
            : weeklyProjKg > 0.02
              ? (lang === "en" ? "gain" : "زيادة")
              : (lang === "en" ? "stable" : "ثبات");
          const insightCopy = activeDays === 0
            ? (lang === "en" ? "Log your meals to see the projection" : "سجّل وجباتك ليظهر التوقّع")
            : Math.abs(weeklyProjKg) < 0.02
              ? (lang === "en" ? "At your current pace: no noticeable weight change" : "بمعدلك الحالي: بدون تغيّر ملحوظ في الوزن")
              : (lang === "en"
                  ? `At your current pace → ${weeklyDirWord} ${weeklyProjKgAbs.toFixed(2)} kg per week`
                  : `بمعدلك الحالي → ${weeklyDirWord} ${weeklyProjKgAbs.toFixed(2)} كجم أسبوعياً`);

          // Simpler friendly primary status — positive when it supports the goal, gentle otherwise
          // Suffix reads as a clarifying parenthetical that ties the status to the user's actual goal
          // (e.g. "عجز صحي (يدعم نزول وزنك)") so beginners don't have to guess.
          const goalSupportSuffix = goal === "lose"
            ? (lang === "en" ? "supports weight loss" : "يدعم نزول وزنك")
            : goal === "gain"
              ? (lang === "en" ? "supports weight gain" : "يدعم زيادة وزنك")
              : (lang === "en" ? "supports weight stability" : "يدعم ثبات وزنك");
          const primaryStatusCopy =
            activeDays === 0
              ? (lang === "en" ? "Log your meals to get an analysis" : "سجّل وجباتك للحصول على تحليل")
              : weeklyGoodDir
                ? goal === "lose" ? (lang === "en" ? `✨ Excellent — healthy deficit (${goalSupportSuffix})` : `✨ ممتاز — عجز صحي (${goalSupportSuffix})`) :
                  goal === "gain" ? (lang === "en" ? `✨ Excellent — healthy surplus (${goalSupportSuffix})` : `✨ ممتاز — فائض صحي (${goalSupportSuffix})`) :
                  (lang === "en" ? `✨ Excellent — close to maintenance (${goalSupportSuffix})` : `✨ ممتاز — قريب من الحفاظ (${goalSupportSuffix})`)
                : goal === "lose"
                  ? (lang === "en" ? "❕ You need a bigger deficit (to lose noticeably)" : "❕ تحتاج عجزاً أكبر (لتنزل بشكل ملحوظ)")
                  : goal === "gain"
                    ? (lang === "en" ? "❕ You need a bigger surplus (to gain noticeably)" : "❕ تحتاج فائضاً أكبر (لتزيد بشكل ملحوظ)")
                    : (lang === "en" ? "❕ Slightly off maintenance" : "❕ ابتعدت قليلاً عن التوازن");

          // Missing TDEE guard — happens only if the user hasn't completed profile
          if (tdee <= 0) {
            return (
              <ChartCard title={lang === "en" ? "Calorie balance" : "الفارق الحراري"} subtitle={lang === "en" ? "Needs your data (height/weight/age/activity)" : "يحتاج بياناتك (الطول/الوزن/العمر/النشاط)"} testid="chart-balance">
                <div className="text-xs text-neutral-500 text-center py-6 leading-relaxed">
                  {lang === "en" ? 'Complete your info in "Update Goals" to calculate your body\'s natural burn (TDEE) and show the calorie balance accurately.' : 'أكمل بياناتك من صفحة "تحديث الأهداف" لحساب حرق جسمك الطبيعي (TDEE) وعرض الفارق الحراري بدقة.'}
                </div>
              </ChartCard>
            );
          }

          return (
            <ChartCard title={lang === "en" ? "Calorie balance" : "الفارق الحراري"} subtitle={lang === "en" ? "Difference between what you eat and what your body + workouts burn" : "الفرق بين ما تأكله وما يحرقه جسمك + التمارين"} testid="chart-balance">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartSeries} margin={{ top: 20, right: 22, left: 22, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke="#F3F4F6" />
                  <XAxis dataKey="label" fontSize={viewMode === "week" ? 11 : 10} tick={{ fill: "#78706A", fontWeight: 600 }} axisLine={false} tickLine={false} reversed={lang !== "en"} interval={0} height={22} />
                  <YAxis hide />
                  {goal === "lose" && (
                    <ReferenceArea y1={-100000} y2={0} fill={goodColor} fillOpacity={0.06} />
                  )}
                  {goal === "gain" && (
                    <ReferenceArea y1={0} y2={100000} fill={goodColor} fillOpacity={0.06} />
                  )}
                  <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="4 4" strokeWidth={1} />
                  <Tooltip
                    cursor={false}
                    content={makeBalanceTooltip({ ...tipStepsProps, tdee })}
                  />
                  <Line type="monotone" dataKey="balance" name="الفارق" stroke="#9CA3AF" strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      if (cy == null) return null;
                      if (payload && payload.has_meals === false) return null;
                      if (payload && payload.is_today) {
                        return <circle cx={cx} cy={cy} r={5} fill="#fff" stroke={colorForBalance(payload.balance || 0)} strokeWidth={2} strokeDasharray="2 2" />;
                      }
                      return <circle cx={cx} cy={cy} r={4} fill={colorForBalance(payload.balance || 0)} stroke="#fff" strokeWidth={1.5} />;
                    }}
                    activeDot={{ r: 6 }}
                  >
                    <LabelList dataKey="balance"
                      content={({ x, y, value, index }) => {
                        if (value == null) return null;
                        const point = chartSeries[index];
                        if (point && point.has_meals === false) return null;
                        if (point && point.is_today) return null;
                        const color = colorForBalance(Number(value));
                        const text = Math.round(Number(value));
                        return (
                          <text x={x} y={y - 8} textAnchor="middle" fontSize={10} fontWeight={700} fill={color}>
                            {text > 0 ? `+${text}` : text}
                          </text>
                        );
                      }}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>

              {/* Inline legend — explains every visual guide in the chart so
                  users don't have to guess. Colours match the goal-aware chart
                  palette (colorForBalance) and wording adapts to the user's goal. */}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[10px] px-1" dir="rtl" data-testid="balance-legend">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-4 border-t-2 border-dashed border-neutral-400"></span>
                  <span className="text-neutral-500 font-semibold">{lang === "en" ? "Maintenance line (weight doesn't change)" : "خط الثبات (وزنك لا ينزل ولا يزيد)"}</span>
                </div>
                {goal !== "maintain" && (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm border" style={{ background: goodColor, borderColor: goodColor }}></span>
                    <span className="font-semibold" style={{ color: goodColor }}>
                      {goal === "lose"
                        ? (lang === "en" ? "Deficit point (supports weight loss)" : "نقطة عجز (يدعم نقص وزنك)")
                        : (lang === "en" ? "Surplus point (supports weight gain)" : "نقطة فائض (يدعم زيادة وزنك)")}
                    </span>
                  </div>
                )}
                {goal !== "maintain" && (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm border" style={{ background: badColor, borderColor: badColor }}></span>
                    <span className="font-semibold" style={{ color: badColor }}>
                      {goal === "lose"
                        ? (lang === "en" ? "Surplus point (against your goal)" : "نقطة فائض (يعاكس هدفك)")
                        : (lang === "en" ? "Deficit point (against your goal)" : "نقطة عجز (يعاكس هدفك)")}
                    </span>
                  </div>
                )}
                {goal === "maintain" && (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm border" style={{ background: goodColor, borderColor: goodColor }}></span>
                    <span className="font-semibold" style={{ color: goodColor }}>{lang === "en" ? "Near-maintenance point (supports your weight)" : "نقطة قريبة من الثبات (يدعم وزنك)"}</span>
                  </div>
                )}
              </div>

              {/* ══════════ BEGINNER-FRIENDLY SUMMARY ══════════
                  Just ONE prediction card + ONE status pill by default.
                  Everything else (TDEE, day counts, cumulative, tips) is behind "المزيد".
              */}
              <div className="mt-3 space-y-2" dir="rtl" data-testid="balance-simple">
                {/* "Today so far" mini-card — gives IMMEDIATE feedback when the user logs
                    a meal or exercise for today. Otherwise it feels like nothing changed
                    because today is (correctly) excluded from weekly averages. */}
                {todayHasMeals && (() => {
                  const todayPoint = chartSeries.find((s) => s.is_today);
                  const todayBalance = todayPoint?.balance ?? 0;
                  const todayIn  = todayPoint?.cal_in  || 0;
                  const todayOut = todayPoint?.cal_out || 0;
                  const todayColor = colorForBalance(todayBalance);
                  return (
                    <div className="rounded-2xl p-3 bg-white border-[1.5px]"
                         style={{ borderColor: `${todayColor}55` }}
                         dir="rtl"
                         data-testid="balance-today-live">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-semibold text-neutral-600 flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-white border-[1.5px] border-dashed" style={{ borderColor: todayColor }}></span>
                          {lang === "en" ? "Today so far" : "اليوم حتى الآن"}
                        </span>
                        <span className="text-[9px] text-neutral-400">{lang === "en" ? "Not counted in weekly projection" : "لا يُحسب في التوقّع الأسبوعي"}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-[10px] text-neutral-500">
                          <div className="flex flex-col items-center">
                            <span className="num font-bold text-neutral-700 text-sm" dir="ltr">{todayIn}</span>
                            <span>{lang === "en" ? "Intake" : "مُتَناوَل"}</span>
                          </div>
                          <span className="text-neutral-300">−</span>
                          <div className="flex flex-col items-center">
                            <span className="num font-bold text-amber-600 text-sm" dir="ltr">{tdee}</span>
                            <span>TDEE</span>
                          </div>
                          <span className="text-neutral-300">−</span>
                          <div className="flex flex-col items-center">
                            <span className="num font-bold text-red-500 text-sm" dir="ltr">{todayOut}</span>
                            <span>{lang === "en" ? "Workouts" : "تمارين"}</span>
                          </div>
                        </div>
                        <span className="num font-black text-lg" style={{ color: todayColor }} dir="ltr">
                          {todayBalance > 0 ? `+${todayBalance}` : todayBalance}
                          <span className="text-[9px] font-semibold mr-1">{lang === "en" ? "cal" : "سعرة"}</span>
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Status pill — the single at-a-glance signal that stays
                    visible by default. Everything else (weekly-projection card,
                    TDEE, day counts, cumulative, tips) is behind "المزيد". */}
                <div className="rounded-full px-3 py-1.5 text-[11px] font-bold text-center"
                     style={{ background: `${weeklyColor}18`, color: weeklyColor }}
                     data-testid="balance-status">
                  {primaryStatusCopy}
                </div>

                {/* Details toggle */}
                <button
                  onClick={() => setBalanceDetailsOpen((v) => !v)}
                  className="w-full flex items-center justify-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-700 font-semibold py-1.5 rounded-lg hover:bg-neutral-50 transition-colors"
                  data-testid="balance-details-toggle"
                  aria-expanded={balanceDetailsOpen}
                >
                  <span>{balanceDetailsOpen ? (lang === "en" ? "Hide details" : "إخفاء التفاصيل") : (lang === "en" ? "More details" : "المزيد من التفاصيل")}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${balanceDetailsOpen ? "rotate-180" : ""}`} />
                </button>

                {/* ══════════ DETAILED VIEW — for advanced users ══════════ */}
                {balanceDetailsOpen && (
                  <div className="space-y-2 pt-1" data-testid="balance-details">
                    {/* Primary prediction card — the "if you keep this pace"
                        insight. Hidden by default because it can distract
                        beginners who just want the pill status. */}
                    <div className="rounded-2xl p-3.5 border-[1.5px]"
                         style={{ borderColor: `${weeklyColor}55`, background: `${weeklyColor}0d` }}
                         data-testid="balance-weekly-projection">
                      <div className="text-[11px] text-neutral-600 font-semibold mb-1.5">{lang === "en" ? "Weight projection" : "توقّع الوزن"}</div>
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-neutral-500">{lang === "en" ? "Weekly" : "أسبوعياً"}</span>
                          <span className="num font-black text-2xl leading-tight" style={{ color: weeklyColor }} dir="ltr">
                            {weeklyProjKg === 0 ? "0.00" : (weeklyProjKg > 0 ? `+${weeklyProjKgAbs.toFixed(2)}` : `−${weeklyProjKgAbs.toFixed(2)}`)}
                            <span className="text-[11px] font-semibold mr-1 opacity-70">{lang === "en" ? "kg" : "كجم"}</span>
                          </span>
                        </div>
                        <div className="text-[11px] font-bold text-end max-w-[55%] leading-tight" style={{ color: weeklyColor }}>
                          {insightCopy}
                        </div>
                      </div>
                    </div>
                    {/* Today-incomplete legend */}
                    {todayHasMeals && (
                      <div className="flex items-center justify-center gap-1.5 text-[10px] text-neutral-500" data-testid="balance-today-incomplete-hint">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-white border-[1.5px] border-dashed border-neutral-400"></span>
                        <span>{lang === "en" ? "Today — in progress · not counted in average" : "اليوم — قيد الإكمال · لا يُحسب في المتوسط"}</span>
                      </div>
                    )}

                    {/* TDEE */}
                    <div className="flex items-center justify-between gap-2 bg-amber-50/60 border border-amber-100 rounded-xl px-2.5 py-1.5">
                      <span className="text-[10px] text-amber-800 font-semibold">{lang === "en" ? "Your body's daily burn (TDEE)" : "حرق جسمك اليومي (TDEE)"}</span>
                      <span className="text-[11px] font-black text-amber-700" dir="ltr">
                        {tdee} <span className="text-[9px] font-semibold text-amber-600">{lang === "en" ? "cal" : "سعرة"}</span>
                      </span>
                    </div>

                    {/* Cumulative + daily average — the ORDER swaps by period:
                        week/month → cumulative is primary (concrete kcal towards fat gain/loss)
                        year      → daily average is primary (cumulative gets huge & unwieldy) */}
                    {viewMode !== "year" && (
                      <div className="rounded-2xl p-3 border" style={{ borderColor: `${cumColor}40`, background: `${cumColor}0d` }}>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-neutral-600 font-semibold">
                            {lang === "en"
                              ? (cumulative < 0 ? `Deficit this ${periodLabelAr} so far` : cumulative > 0 ? `Surplus this ${periodLabelAr} so far` : `${periodLabelAr[0].toUpperCase() + periodLabelAr.slice(1)} balance`)
                              : (cumulative < 0 ? `عجز ${periodLabelAr} حتى الآن` : cumulative > 0 ? `فائض ${periodLabelAr} حتى الآن` : `فارق ${periodLabelAr}`)}
                          </span>
                          <span className="num font-black text-xl" style={{ color: cumColor }} dir="ltr">
                            {cumulative > 0 ? `+${cumulative}` : cumulative}
                            <span className="text-[10px] font-semibold mr-1 opacity-80">{lang === "en" ? "cal" : "سعرة"}</span>
                          </span>
                        </div>
                      </div>
                    )}

                    <div className={`flex items-center justify-between rounded-2xl px-3 py-2 ${viewMode === "year" ? "border-2 shadow-sm" : "bg-neutral-50"}`}
                         style={viewMode === "year" ? { borderColor: `${weeklyColor}55`, background: `${weeklyColor}0d` } : undefined}>
                      <div className="flex flex-col text-right">
                        <span className={`${viewMode === "year" ? "text-[13px] font-bold" : "text-xs"} text-neutral-700`}>{lang === "en" ? "Average daily balance" : "متوسط الفارق اليومي"}</span>
                        <span className="text-[9px] text-neutral-400">{lang === "en" ? `Based on ${activeDays} of ${totalDays} logged days` : `محسوب على ${activeDays} من ${totalDays} يوم مسجّل`}</span>
                      </div>
                      <span className={`num font-black ${viewMode === "year" ? "text-2xl" : "text-lg"}`} style={{ color: weeklyColor }} dir="ltr">
                        {avg > 0 ? `+${avg}` : avg}
                        <span className="text-[10px] font-semibold text-neutral-400 mr-1">{lang === "en" ? "cal" : "سعرة"}</span>
                      </span>
                    </div>

                    {viewMode === "year" && (
                      <div className="rounded-2xl p-3 border" style={{ borderColor: `${cumColor}40`, background: `${cumColor}0d` }}>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-neutral-600 font-semibold">
                            {lang === "en"
                              ? (cumulative < 0 ? `Cumulative ${periodLabelAr} deficit` : cumulative > 0 ? `Cumulative ${periodLabelAr} surplus` : `${periodLabelAr[0].toUpperCase() + periodLabelAr.slice(1)} balance`)
                              : (cumulative < 0 ? `عجز ${periodLabelAr} تراكمي` : cumulative > 0 ? `فائض ${periodLabelAr} تراكمي` : `فارق ${periodLabelAr}`)}
                          </span>
                          <span className="num font-black text-base" style={{ color: cumColor }} dir="ltr">
                            {cumulative > 0 ? `+${cumulative}` : cumulative}
                            <span className="text-[10px] font-semibold mr-1 opacity-80">{lang === "en" ? "cal" : "سعرة"}</span>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Day-count breakdown — colours AND descriptions flip based on goal:
                         lose  → deficit=good(green), balanced=amber, surplus=bad(red)
                         gain  → surplus=good(green), balanced=amber, deficit=bad(red)
                         maintain → balanced=good(green), deficit/surplus=amber */}
                    {(() => {
                      const dayCatColor = (cat) => {
                        if (goal === "lose")
                          return cat === "deficit" ? goodColor : cat === "surplus" ? badColor : neutralColor;
                        if (goal === "gain")
                          return cat === "surplus" ? goodColor : cat === "deficit" ? badColor : neutralColor;
                        return cat === "balanced" ? goodColor : neutralColor;
                      };
                      // Parenthetical hint tells the user WHAT that column means FOR THEIR GOAL.
                      const dayCatHint = (cat) => {
                        if (goal === "lose")
                          return cat === "deficit" ? (lang === "en" ? "supports loss" : "يدعم النزول")
                               : cat === "balanced" ? (lang === "en" ? "stable" : "ثبات")
                               : (lang === "en" ? "against goal" : "يعاكس الهدف");
                        if (goal === "gain")
                          return cat === "surplus" ? (lang === "en" ? "supports gain" : "يدعم الزيادة")
                               : cat === "balanced" ? (lang === "en" ? "stable" : "ثبات")
                               : (lang === "en" ? "against goal" : "يعاكس الهدف");
                        return cat === "balanced" ? (lang === "en" ? "supports maintenance" : "يدعم الحفاظ")
                             : cat === "deficit"  ? (lang === "en" ? "below maintenance" : "تحت الحفاظ")
                             : (lang === "en" ? "above maintenance" : "فوق الحفاظ");
                      };
                      const box = (cat, label, val) => {
                        const col = dayCatColor(cat);
                        return (
                          <div className="rounded-xl py-1.5 px-1" style={{ background: `${col}14` }}>
                            <div className="text-[10px] font-semibold leading-tight" style={{ color: col }}>{label}</div>
                            <div className="text-[8.5px] font-medium leading-tight opacity-80" style={{ color: col }}>({dayCatHint(cat)})</div>
                            <div className="num font-black text-sm mt-0.5" style={{ color: col }}>{val}</div>
                          </div>
                        );
                      };
                      return (
                        <div className="grid grid-cols-3 gap-1.5 text-center">
                          {box("deficit",  lang === "en" ? "Deficit days"  : "أيام عجز",  deficitDays)}
                          {box("balanced", lang === "en" ? "Balanced"      : "متوازن",   balancedDays)}
                          {box("surplus",  lang === "en" ? "Surplus days"  : "أيام فائض", surplusDays)}
                        </div>
                      );
                    })()}

                    <div className="text-[10px] text-neutral-500 text-center leading-relaxed">
                      💡 {tipCopy}
                    </div>
                  </div>
                )}
              </div>
            </ChartCard>
          );
        })()}

        <ChartCard title={lang === "en" ? "Water (ml)" : "الماء (مل)"} testid="chart-water">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={chartSeries} margin={{ top: 10, right: 22, left: 22, bottom: 4 }}>
              <CartesianGrid vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="label" fontSize={viewMode === "week" ? 11 : 10} tick={{ fill: "#78706A", fontWeight: 600 }} axisLine={false} tickLine={false} reversed={lang !== "en"} interval={0} height={22} />
              <YAxis hide />
              <Tooltip cursor={false} content={makeTooltip({ ...tipStepsProps, unit: lang === "en" ? "ml" : "مل" })} />
              <Bar dataKey="water_ml" name={lang === "en" ? "Water" : "الماء"} fill="#38BDF8" radius={[10, 10, 0, 0]} activeBar={false} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
          <BarValueRow series={chartSeries} valueKey="water_ml" color="#0EA5E9" testid="chart-water-vals" tiny={viewMode === "year"} />
          <AvgRow label={lang === "en" ? "Avg daily water" : "متوسط الماء يومياً"} value={data?.avg_water_ml} color="#0EA5E9" />
        </ChartCard>

        <ChartCard title={lang === "en" ? "Steps" : "الخطوات"} testid="chart-steps">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={chartSeries} margin={{ top: 10, right: 22, left: 22, bottom: 4 }}>
              <CartesianGrid vertical={false} stroke="#F3F4F6" />
              <XAxis dataKey="label" fontSize={viewMode === "week" ? 11 : 10} tick={{ fill: "#78706A", fontWeight: 600 }} axisLine={false} tickLine={false} reversed={lang !== "en"} interval={0} height={22} />
              <YAxis hide />
              <Tooltip cursor={false} content={makeTooltip({ ...tipStepsProps, unit: lang === "en" ? "steps" : "خطوة" })} />
              <Bar dataKey="steps" name={lang === "en" ? "Steps" : "الخطوات"} fill="#F97316" radius={[10, 10, 0, 0]} activeBar={false} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
          <BarValueRow series={chartSeries} valueKey="steps" color="#F97316" testid="chart-steps-vals" tiny={viewMode === "year"} />
          <AvgRow label={lang === "en" ? "Avg daily steps" : "متوسط الخطوات يومياً"} value={data?.avg_steps} color="#F97316" />
        </ChartCard>
      </div>

      {/* Coach-only calendar (planner + journal) — placed AFTER the Steps
          chart per user request. Gated by the trainee's `include_calendar`
          share toggle so hidden calendars never render. Reads:
          profile → charts (weight/calories/macros/steps) → calendar → photos. */}
      {isSharedMode && shareCfg?.include_meals !== false && (
        <div className="mt-4">
          <SharedMealsCard />
        </div>
      )}
      {isSharedMode && shareCfg?.include_calendar !== false && (
        <div className="mt-4">
          <SharedCalendarCard />
        </div>
      )}

      {weightModalOpen && (
        <WeightLogModal
          onClose={() => setWeightModalOpen(false)}
          currentWeight={current}
          onSaved={(resp) => {
            setWeightModalOpen(false);
            if (resp?.is_historical) {
              toast.info(lang === "en" ? "Weight saved as historical record 📅 (calories not recalculated — not the latest measurement)" : "تم حفظ الوزن كسجل تاريخي 📅 (لم يتم إعادة حساب السعرات لأنه ليس أحدث قياس)");
            } else if (resp?.recomputed) {
              toast.success(lang === "en" ? "Weight updated and calories recalculated ✅" : "تم تحديث الوزن وإعادة حساب السعرات ✅");
            } else {
              toast.success(lang === "en" ? "Weight updated ✅ (pro mode — no auto-recalculation)" : "تم تحديث الوزن ✅ (الوضع المحترف — لا يتم إعادة الحساب تلقائياً)");
            }
            // Refresh user, weight-trend chart, goal history AND the aggregated summary
            refresh?.();
            loadRecentPhotos();
            loadWeightHistory();
            loadGoalHistory();
            setData(null);
            setTimeout(() => {
              const q = new URLSearchParams();
              if (viewMode === "week") { q.set("range","week"); q.set("week_start", toISO(weekStart)); }
              else if (viewMode === "month") { q.set("range","month"); q.set("year", monthCursor.year); q.set("month", monthCursor.month); }
              else { q.set("range","year"); q.set("year", yearCursor); }
              api.get(`/progress/summary?${q.toString()}`).then(({ data }) => setData(data));
            }, 100);
          }}
        />
      )}
      {goalModalOpen && (
        <GoalWeightModal
          onClose={() => setGoalModalOpen(false)}
          currentTarget={target}
          currentWeight={current}
          onSaved={async (payload) => {
            setGoalModalOpen(false);
            await refresh();
            loadWeightHistory();
            loadGoalHistory();
            setData(null);
            setTimeout(() => {
              const q = new URLSearchParams();
              if (viewMode === "week") { q.set("range","week"); q.set("week_start", toISO(weekStart)); }
              else if (viewMode === "month") { q.set("range","month"); q.set("year", monthCursor.year); q.set("month", monthCursor.month); }
              else { q.set("range","year"); q.set("year", yearCursor); }
              api.get(`/progress/summary?${q.toString()}`).then(({ data }) => setData(data));
            }, 100);
            if (payload?.recomputed) {
              toast.success(lang === "en" ? "Goal updated & calories/macros recalculated ✅" : "تم تحديث الهدف وإعادة حساب السعرات والماكروز ✅");
            } else {
              toast.success(lang === "en" ? "Target weight updated ✅" : "تم تحديث الوزن المرغوب ✅");
            }
          }}
        />
      )}
    </div>
  );
}
